import { Link } from 'react-router-dom';
import ResourceLayout from '../../components/resources/ResourceLayout';
import useSEO from '../../hooks/useSEO';
import { canonicalUrl } from '../../utils/testSite';
import { guidePageSchema } from '../../utils/schema';

const ARTICLE_SCHEMA = {
  '@type': 'Article',
  headline: 'Your Arizona License Expired. Now What?',
  description: 'Missed your Arizona renewal? What the $125 late fee covers, the one-year deadline, what you can\'t do while expired, and how to fix it fast.',
  datePublished: '2026-06-01',
  dateModified: '2026-06-10',
  author: { '@type': 'Organization', name: 'Steady Education' },
  publisher: { '@type': 'EducationalOrganization', name: 'Steady Education', url: 'https://steadyeducation.com' },
};

const RELATED = [
  { url: '/arizona/how-to-renew', title: 'How to Renew, Step by Step', desc: 'The full renewal in five steps: CE, Licensee Login, Renew, pay.' },
  { url: '/when-does-my-arizona-license-expire', title: 'When Does My License Expire?', desc: 'How to check your exact renewal date and avoid the late fee next time.' },
  { url: '/how-fast-can-i-finish-arizona-ce', title: 'How Fast Can You Finish 24 Hours?', desc: 'The 9-hour daily cap and the minimum 3-day schedule.' },
  { url: '/how-to-submit-ce-to-adre', title: 'How to Submit CE to ADRE', desc: 'Your school doesn\'t report your courses — you do.' },
];

export default function LateRenewalPage() {
  useSEO({
    title: 'Expired AZ Real Estate License? Late Renewal Explained',
    description: 'Missed your Arizona renewal? What the $125 late fee covers, the one-year deadline, what you can\'t do while expired, and how to fix it fast.',
    url: canonicalUrl('/arizona-license-expired-late-renewal'),
    jsonLd: guidePageSchema(ARTICLE_SCHEMA, {
      title: 'Late Renewal',
      url: canonicalUrl('/arizona-license-expired-late-renewal'),
    }),
  });

  return (
    <ResourceLayout
      breadcrumb="Late Renewal"
      title="Your Arizona License Expired. Now What?"
      subtitle="Take a breath — if it's been less than a year, this is fixable. Here's exactly what happens and what to do."
      relatedGuides={RELATED}
    >
      <p>Not expired yet? Follow the <Link to="/arizona/how-to-renew">step-by-step Arizona license renewal guide</Link> instead and avoid the late fee entirely.</p>

      <h2>The moment your license expires</h2>
      <ul>
        <li>You <strong>cannot perform any real estate activity</strong> — no showings, no offers, no negotiations, no referral fees on new business. Practicing on an expired license is a violation that can draw ADRE discipline beyond the late fee.</li>
        <li>Your renewal fee increases from $60 to <strong>$125</strong> (salespersons) or from $120 to <strong>$245</strong> (brokers) — per ADRE's official fee schedule.</li>
        <li>Your broker can see your expired status in the public database, and so can anyone else.</li>
      </ul>

      <h2>The one-year window</h2>
      <p>Arizona gives you <strong>up to one year after expiration</strong> to renew late. You'll need your 24 CE hours completed and submitted, plus the late fee. There are no extensions beyond that year.</p>

      <h2>After one year</h2>
      <p>Your license <strong>can't be renewed</strong>. You're starting over — pre-licensing education, exam, original application. The cost of missing the window isn't $65; it's potentially months out of business.</p>

      <h2>Fastest path back if you're expired right now</h2>
      <ol>
        <li>Count your completed CE hours in your Licensee Login (some may already be submitted).</li>
        <li>Knock out what's missing — remember the 9-hour daily cap means a full 24 hours takes at least 3 days. Online, self-paced courses are the fastest route.</li>
        <li>Submit every certificate in the portal, renew, pay the late fee.</li>
      </ol>

      <div style={inlineCta}>
        <strong>Complete package — $49.99, instant certificates.</strong> Every required category, every 2025 mandate, ready to submit to ADRE the same day.
        <div style={{ marginTop: 14 }}>
          <Link to="/arizona/courses" style={inlineCtaBtn}>Get the package →</Link>
        </div>
      </div>

      <h2>While you're expired, the clock matters more than the money</h2>
      <p>Every week inactive is lost pipeline. If you're 10 months past expiration, treat this as an emergency — at 12 months the door closes permanently.</p>

      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', fontStyle: 'italic', marginTop: 32 }}>
        Fees per azre.gov fee schedule, June 2026.
      </p>
    </ResourceLayout>
  );
}

const inlineCta = { background: '#FAF7F2', border: '1px solid #d4c5ad', borderRadius: 12, padding: '20px 24px', marginTop: 24 };
const inlineCtaBtn = { display: 'inline-block', background: '#2C453E', color: 'white', padding: '10px 22px', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: 14 };
